import React, { useState, useRef, useMemo } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { Upload, Check, ChevronDown } from 'lucide-react';
import RichTextEditor from './richtexteditor';
import { FancyMultiSelect } from './fancy_multi_select_component';

// ─────────────────────────────────────────────────────────────────────────────
// DESIGN TOKENS — Indigo Enterprise (mirrors AttributeModals.jsx)
// ─────────────────────────────────────────────────────────────────────────────
const C = {
  body:        "#f3f4f6",
  card:        "#ffffff",
  blueTint:    "#eef2ff",
  border:      "#d1d5db",
  borderMid:   "#9ca3af",
  borderFocus: "#4338ca",
  ink:         "#111827",
  sub:         "#1f2937",
  muted:       "#4b5563",
  faint:       "#9ca3af",
  indigo50:    "#eef2ff",
  indigo100:   "#e0e7ff",
  indigo200:   "#c7d2fe",
  indigo500:   "#6366f1",
  indigo600:   "#4338ca",
  indigo700:   "#3730a3",
  indigo800:   "#312e81",
  green500:    "#22c55e",
  green600:    "#16a34a",
  red50:       "#fee2e2",
  red200:      "#fca5a5",
  red600:      "#b91c1c",
  gradStart:   "#312e81",
  gradMid:     "#4338ca",
  gradEnd:     "#6d28d9",
  statusPending:  "#F59E0B",
  statusDone:     "#10B981",
  statusOverdue:  "#EF4444",
  priorityHigh:   "#EF4444",
  priorityMedium: "#F59E0B",
  priorityLow:    "#3B82F6",
};

const ff     = "'Inter', system-ui, sans-serif";
const ffMono = "'JetBrains Mono', 'Cascadia Code', Consolas, monospace";

// ─────────────────────────────────────────────────────────────────────────────
// GLOBAL STYLES — injected once
// ─────────────────────────────────────────────────────────────────────────────
let _stylesInjected = false;
function injectStyles() {
  if (_stylesInjected || typeof document === "undefined") return;
  _stylesInjected = true;

  const link = document.createElement("link");
  link.rel  = "stylesheet";
  link.href = "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap";
  document.head.appendChild(link);

  const style = document.createElement("style");
  style.textContent = `
    @keyframes _backdropIn { from{opacity:0} to{opacity:1} }
    @keyframes _modalSlideIn { from{opacity:0;transform:translateY(20px) scale(.97)} to{opacity:1;transform:translateY(0) scale(1)} }
    @keyframes _fadeUp { from{opacity:0;transform:translateY(6px)} to{opacity:1;transform:translateY(0)} }

    ._tm-overlay {
      position:fixed; inset:0; z-index:1050;
      background:rgba(15,23,42,.50);
      backdrop-filter:blur(5px);
      display:flex; align-items:center; justify-content:center;
      padding:20px;
      animation:_backdropIn .2s ease;
    }
    ._tm-shell {
      font-family:${ff};
      width:100%; max-width:780px;
      background:${C.card};
      border-radius:16px;
      box-shadow:0 32px 80px rgba(15,23,42,.35), 0 8px 24px rgba(15,23,42,.08);
      overflow:hidden;
      display:flex; flex-direction:column;
      max-height:92vh;
      border:1px solid ${C.border};
      animation:_modalSlideIn .28s cubic-bezier(.34,1.56,.64,1);
    }
    ._tm-shell-sm { max-width:520px; }
    ._tm-header {
      background:linear-gradient(135deg, ${C.gradStart} 0%, ${C.gradMid} 55%, ${C.gradEnd} 100%);
      padding:22px 26px 20px;
      display:flex; align-items:flex-start; justify-content:space-between;
      position:relative; overflow:hidden; flex-shrink:0;
    }
    ._tm-header::before {
      content:''; position:absolute; right:-40px; top:-40px;
      width:160px; height:160px; border-radius:50%;
      background:rgba(255,255,255,.07); pointer-events:none;
    }
    ._tm-header::after {
      content:''; position:absolute; right:30px; bottom:-60px;
      width:120px; height:120px; border-radius:50%;
      background:rgba(255,255,255,.04); pointer-events:none;
    }
    ._tm-title {
      font-size:20px; font-weight:800; color:#fff;
      letter-spacing:-.03em; line-height:1.2; font-family:${ff};
    }
    ._tm-subtitle {
      font-size:12px; color:rgba(255,255,255,.65);
      margin-top:3px; font-family:${ff};
    }
    ._tm-close-btn {
      width:30px; height:30px; border-radius:8px;
      border:1px solid rgba(255,255,255,.25);
      background:rgba(255,255,255,.12);
      color:#fff; display:flex; align-items:center; justify-content:center;
      cursor:pointer; flex-shrink:0; margin-left:12px;
      outline:none; transition:background .15s; z-index:2; position:relative;
    }
    ._tm-close-btn:hover { background:rgba(255,255,255,.24); }
    ._tm-body {
      padding:24px 26px;
      overflow-y:auto; flex:1;
      background:${C.body};
    }
    ._tm-body::-webkit-scrollbar { width:4px; }
    ._tm-body::-webkit-scrollbar-track { background:transparent; }
    ._tm-body::-webkit-scrollbar-thumb { background:#d1d5db; border-radius:4px; }
    ._tm-footer {
      padding:14px 26px;
      border-top:1px solid ${C.border};
      background:${C.card};
      display:flex; justify-content:flex-end; gap:10px;
      flex-shrink:0;
    }
    ._tm-form-grid {
      display:grid; grid-template-columns:1fr 1fr; gap:18px;
    }
    ._tm-form-grid-full { grid-column:1/-1; }
    ._tm-label {
      display:flex; align-items:center; gap:6px;
      font-size:11px; font-weight:700; color:${C.muted};
      letter-spacing:.06em; text-transform:uppercase;
      font-family:${ff}; margin-bottom:6px;
    }
    ._tm-status-dot {
      display:inline-block; width:8px; height:8px; border-radius:50%;
    }
    ._tm-input {
      width:100%; box-sizing:border-box;
      padding:10px 13px;
      border:1.5px solid ${C.borderMid};
      border-radius:8px;
      font-size:14px; color:${C.ink};
      background:${C.card};
      outline:none; font-family:${ff};
      transition:border-color .15s, box-shadow .15s;
    }
    ._tm-input:focus {
      border-color:${C.borderFocus};
      box-shadow:0 0 0 2px #4338ca33;
    }
    ._tm-input:disabled {
      background:${C.body}; color:${C.muted}; cursor:not-allowed;
    }
    ._tm-select {
      width:100%; box-sizing:border-box;
      padding:10px 36px 10px 13px;
      border:1.5px solid ${C.borderMid};
      border-radius:8px;
      font-size:14px; color:${C.ink};
      background:${C.card} url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%234338ca' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E") no-repeat right 12px center / 14px;
      -webkit-appearance:none; -moz-appearance:none; appearance:none;
      outline:none; font-family:${ff};
      transition:border-color .15s, box-shadow .15s;
      cursor:pointer;
    }
    ._tm-select:focus {
      border-color:${C.borderFocus};
      box-shadow:0 0 0 2px #4338ca33;
    }
    ._tm-textarea {
      width:100%; box-sizing:border-box;
      padding:10px 13px;
      border:1.5px solid ${C.borderMid};
      border-radius:8px;
      font-size:14px; color:${C.ink};
      background:${C.card};
      outline:none; font-family:${ff};
      transition:border-color .15s, box-shadow .15s;
      resize:vertical; min-height:80px;
    }
    ._tm-textarea:focus {
      border-color:${C.borderFocus};
      box-shadow:0 0 0 2px #4338ca33;
    }
    ._tm-btn-primary {
      display:inline-flex; align-items:center; justify-content:center; gap:7px;
      padding:0 20px; height:36px; border-radius:8px;
      background:linear-gradient(135deg,${C.indigo700},${C.indigo600});
      color:#fff; border:none; font-size:13px; font-weight:700;
      cursor:pointer; font-family:${ff};
      box-shadow:0 2px 8px rgba(67,56,202,.40);
      transition:opacity .15s, transform .1s;
      letter-spacing:.01em; outline:none;
    }
    ._tm-btn-primary:hover { opacity:.88; transform:translateY(-1px); }
    ._tm-btn-secondary {
      display:inline-flex; align-items:center; justify-content:center; gap:7px;
      padding:0 16px; height:36px; border-radius:8px;
      background:${C.card}; color:#374151;
      border:1.5px solid ${C.borderMid};
      font-size:13px; font-weight:600;
      cursor:pointer; font-family:${ff};
      transition:border-color .12s, color .12s; outline:none;
    }
    ._tm-btn-secondary:hover { border-color:${C.indigo600}; color:${C.indigo600}; }
    ._tm-rich-editor-wrap {
      border-radius:8px; overflow:hidden;
      border:1.5px solid ${C.borderMid};
      background:${C.card};
    }
    ._tm-section-card {
      background:${C.card};
      border:1px solid ${C.border};
      border-radius:10px;
      padding:18px 20px;
      animation:_fadeUp .22s ease;
    }

    /* Multi-select dropdown */
    .editaskselect-container { position:relative; padding:0 !important; height:auto !important; }
    .multi-select {
      display:flex; align-items:center; justify-content:space-between;
      padding:10px 13px; cursor:pointer; min-height:42px;
    }
    .multi-select-text { font-size:14px; color:${C.ink}; font-family:${ff}; flex:1; }
    .multi-select-dropdown {
      position:absolute; top:100%; left:0; right:0; z-index:200;
      background:${C.card}; border:1.5px solid ${C.borderFocus};
      border-radius:8px; box-shadow:0 8px 24px rgba(0,0,0,.12);
      overflow:hidden; margin-top:4px;
    }
    .option-item {
      display:flex; align-items:center; justify-content:space-between;
      padding:10px 14px; cursor:pointer; font-size:13px;
      font-family:${ff}; color:${C.ink};
      transition:background .1s;
    }
    .option-item:hover { background:${C.blueTint}; }
    .option-selected { background:${C.indigo50}; color:${C.indigo700}; font-weight:600; }
    .option-check { color:${C.indigo600}; display:flex; align-items:center; }

    /* Upload area */
    .upload-area {
      border:2px dashed ${C.borderMid}; border-radius:10px;
      padding:36px 24px; text-align:center;
      background:${C.body}; transition:all .2s; cursor:pointer;
    }
    .upload-area.drag-active, .upload-area:hover {
      border-color:${C.indigo600}; background:${C.blueTint};
    }
    .upload-area-content { display:flex; flex-direction:column; align-items:center; gap:10px; }
    .upload-icon { color:${C.faint}; }
    .upload-area.drag-active .upload-icon, .upload-area:hover .upload-icon { color:${C.indigo600}; }
    .upload-title { font-size:14px; font-weight:600; color:${C.ink}; font-family:${ff}; margin:0; }
    .upload-subtitle { font-size:12px; color:${C.faint}; font-family:${ff}; margin:0; }
    .choose-files-btn {
      padding:8px 20px; border-radius:8px;
      background:linear-gradient(135deg,${C.indigo700},${C.indigo600});
      color:#fff; border:none; font-size:13px; font-weight:700;
      cursor:pointer; font-family:${ff};
      box-shadow:0 2px 8px rgba(67,56,202,.35);
      transition:opacity .15s; margin-top:4px;
    }
    .choose-files-btn:hover { opacity:.87; }

    /* Share link row */
    ._tm-share-row {
      display:flex; align-items:center; gap:12px;
      padding:12px 16px; border-radius:8px;
      background:${C.blueTint}; border:1px solid ${C.indigo200};
    }
    ._tm-copy-btn {
      padding:8px 18px; border-radius:8px; white-space:nowrap;
      background:linear-gradient(135deg,${C.indigo700},${C.indigo600});
      color:#fff; border:none; font-size:12px; font-weight:700;
      cursor:pointer; font-family:${ff}; flex-shrink:0;
      box-shadow:0 2px 8px rgba(67,56,202,.35);
      transition:opacity .15s;
    }
    ._tm-copy-btn:hover { opacity:.87; }

    @media (max-width:640px) {
      ._tm-form-grid { grid-template-columns:1fr; }
      ._tm-shell { max-width:100%; margin:10px; }
    }
  `;
  document.head.appendChild(style);
}

// ─────────────────────────────────────────────────────────────────────────────
// SHARED ATOMS
// ─────────────────────────────────────────────────────────────────────────────
function SaveIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/>
      <polyline points="17 21 17 13 7 13 7 21"/>
      <polyline points="7 3 7 8 15 8"/>
    </svg>
  );
}

function StatusDot({ type, value }) {
  const colors = {
    status: { pending: C.statusPending, done: C.statusDone, overdue: C.statusOverdue, "in-progress": C.indigo500 },
    priority: { high: C.priorityHigh, medium: C.priorityMedium, low: C.priorityLow },
  };
  const bg = colors[type]?.[value?.toLowerCase()] || C.faint;
  return <span className="_tm-status-dot" style={{ background: bg }} />;
}

function FormLabel({ children, statusType, statusValue }) {
  return (
    <div className="_tm-label">
      {statusType && statusValue && <StatusDot type={statusType} value={statusValue} />}
      {children}
    </div>
  );
}

// Modal shell with indigo header
function TaskModal({ show, onHide, title, subtitle, children, footer, small }) {
  injectStyles();
  if (!show) return null;

  return (
    <div className="_tm-overlay" onClick={onHide}>
      <div className={`_tm-shell${small ? " _tm-shell-sm" : ""}`} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="_tm-header">
          <div style={{ position: "relative", zIndex: 1 }}>
            <div className="_tm-title">{title}</div>
            {subtitle && <div className="_tm-subtitle">{subtitle}</div>}
          </div>
          <button type="button" className="_tm-close-btn" onClick={onHide} aria-label="Close">
            <svg width="11" height="11" viewBox="0 0 11 11" fill="none">
              <path d="M1 1l9 9M10 1L1 10" stroke="white" strokeWidth="1.6" strokeLinecap="round"/>
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="_tm-body">
          {children}
        </div>

        {/* Footer */}
        {footer && <div className="_tm-footer">{footer}</div>}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// VIEW TASK MODAL
// ─────────────────────────────────────────────────────────────────────────────
export const ViewTaskModal = ({
  viewtask, setviewtask,
  task_name, task_user, task_user_list, task_status, task_priority,
  task_created_date, task_due_date, task_note,
  alladdtionalfieldsvalues, setloading,
}) => (
  <TaskModal
    show={viewtask}
    onHide={() => setviewtask(false)}
    title="View Task"
    subtitle="Read-only summary of this task"
  >
    <div className="_tm-section-card" style={{ marginBottom: 18 }}>
      <div className="_tm-form-grid">
        <div>
          <FormLabel>📌 Task</FormLabel>
          <input className="_tm-input" readOnly value={task_name || ''} />
        </div>
        <div>
          <FormLabel statusType="priority" statusValue={task_priority}>Priority</FormLabel>
          <input className="_tm-input" readOnly
            value={task_priority ? task_priority[0].toUpperCase() + task_priority.slice(1).toLowerCase() : ''} />
        </div>
        <div>
          <FormLabel>👥 Assigned To</FormLabel>
          <input className="_tm-input" readOnly
            value={task_user_list.find(u => u.id === task_user)?.name || ''} />
        </div>
        <div>
          <FormLabel statusType="status" statusValue={task_status}>Status</FormLabel>
          <input className="_tm-input" readOnly
            value={task_status ? task_status[0].toUpperCase() + task_status.slice(1).toLowerCase() : ''} />
        </div>
        <div>
          <FormLabel>📆 Created Date</FormLabel>
          <input className="_tm-input" readOnly value={task_created_date || ''} />
        </div>
        <div>
          <FormLabel>📆 Due Date</FormLabel>
          <input className="_tm-input" readOnly value={task_due_date || ''} />
        </div>
      </div>
    </div>

    {task_note && (
      <div className="_tm-section-card" style={{ marginBottom: 18 }}>
        <FormLabel>📝 Description</FormLabel>
        <div className="_tm-rich-editor-wrap">
          <RichTextEditor value={task_note} placeholder="No description" />
        </div>
      </div>
    )}

    {alladdtionalfieldsvalues?.length > 0 && (
      <div className="_tm-section-card">
        <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: '.06em', textTransform: 'uppercase', fontFamily: ff, marginBottom: 14 }}>
          Additional Fields
        </div>
        <div className="_tm-form-grid">
          {alladdtionalfieldsvalues.map(f => (
            <div key={f.field_id}>
              <FormLabel>{f.field_name}</FormLabel>
              <input className="_tm-input" readOnly value={f.field_value || ''} />
            </div>
          ))}
        </div>
      </div>
    )}
  </TaskModal>
);

// ─────────────────────────────────────────────────────────────────────────────
// EDIT TASK MODAL
// ─────────────────────────────────────────────────────────────────────────────
export const EditTaskModal = ({
  edittask, setedittask,
  task_name, settask_name,
  task_user, task_user_list, settask_user,
  task_status, settask_status,
  task_priority, settask_priority,
  task_created_date,
  task_due_date, settask_due_date,
  task_note, settask_note,
  alladdtionalfields, alladdtionalfieldsvalues, setalladdtionalfieldsvalues,
  edit_task, setloading,
}) => {
  const [lastid, setlastid] = useState(0);
  const getId = () => { setlastid(p => p + 1); return lastid + 1; };

  const filteredFields = useMemo(() => {
    return alladdtionalfields.map(field => {
      const match = alladdtionalfieldsvalues.find(v => v.field_id === field.id);
      if (!match) return { id: `New ${getId()}`, field_id: field.id, field_name: field.name, field_type: field.type, field_options: field.options, field_value: '' };
      return { ...match, field_name: field.name, field_type: field.type, field_options: field.options };
    });
  }, [alladdtionalfields, alladdtionalfieldsvalues]);

  const handleChange = (fieldXX, field_id, field_value) => {
    let booleannew = 1;
    let updated = alladdtionalfieldsvalues.map(f => {
      if (f.id === field_id) { booleannew = 0; return { ...f, field_value }; }
      return f;
    });
    if (field_id.includes('New') && booleannew === 1) {
      updated = [...updated, { id: field_id, field_id: fieldXX.field_id, field_name: fieldXX.field_name, field_type: fieldXX.field_type, field_value, field_options: fieldXX.field_options }];
    }
    setalladdtionalfieldsvalues(updated);
  };

  const [showmcqoptions, setshowmcqoptions] = useState(-1);
  const [selectedOptions, setSelectedOptions] = useState([]);

  const handleOptionClick = (fieldXX, value) => {
    setSelectedOptions(prev => {
      const next = prev.includes(value) ? prev.filter(o => o !== value) : [...prev, value];
      const str = next.join(',').replace(/^,/, '');
      handleChange(fieldXX, fieldXX.id, str);
      return next;
    });
  };

  return (
    <TaskModal
      show={edittask}
      onHide={() => setedittask(false)}
      title="Edit Task"
      subtitle="Update the fields below and save your changes"
      footer={
        <button className="_tm-btn-primary" onClick={edit_task}>
          <SaveIcon /> Save Changes
        </button>
      }
    >
      <div className="_tm-section-card" style={{ marginBottom: 18 }}>
        <div className="_tm-form-grid">
          <div>
            <FormLabel>📌 Task</FormLabel>
            <input className="_tm-input" value={task_name || ''} placeholder="Activity"
              onChange={e => settask_name(e.target.value)} />
          </div>
          <div>
            <FormLabel statusType="priority" statusValue={task_priority}>Priority</FormLabel>
            <select className="_tm-select" value={task_priority || ''} onChange={e => settask_priority(e.target.value)}>
              <option value="">Select priority…</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
          </div>
          <div>
            <FormLabel>👥 Assigned To</FormLabel>
            <select className="_tm-select" value={task_user || ''} onChange={e => settask_user(e.target.value)}>
              <option value="">Assign to…</option>
              {task_user_list.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </div>
          <div>
            <FormLabel statusType="status" statusValue={task_status}>Status</FormLabel>
            <select className="_tm-select" value={task_status || ''} onChange={e => settask_status(e.target.value)}>
              <option value="">Select status…</option>
              <option value="done">Done</option>
              <option value="in-progress">In Progress</option>
              <option value="overdue">Overdue</option>
              <option value="pending">Pending</option>
            </select>
          </div>
          <div>
            <FormLabel>📆 Created Date</FormLabel>
            <input className="_tm-input" type="date" disabled value={task_created_date || ''} />
          </div>
          <div>
            <FormLabel>📆 Due Date</FormLabel>
            <input className="_tm-input" type="date" value={task_due_date || ''}
              onChange={e => settask_due_date(e.target.value)} />
          </div>
        </div>
      </div>

      <div className="_tm-section-card" style={{ marginBottom: 18 }}>
        <FormLabel>📝 Description (Optional)</FormLabel>
        <div className="_tm-rich-editor-wrap">
          <RichTextEditor value={task_note} onChange={settask_note} placeholder="Add a description…" />
        </div>
      </div>

      {filteredFields.length > 0 && (
        <div className="_tm-section-card">
          <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: '.06em', textTransform: 'uppercase', fontFamily: ff, marginBottom: 14 }}>
            Additional Fields
          </div>
          <div className="_tm-form-grid">
            {filteredFields.map(f => (
              <div key={f.id}>
                <FormLabel>{f.field_name}</FormLabel>
                {f.field_type === 'text' && (
                  <input className="_tm-input" value={f.field_value || ''} placeholder={f.field_name}
                    onChange={e => handleChange(f, f.id, e.target.value)} />
                )}
                {f.field_type === 'date' && (
                  <input className="_tm-input" type="date" value={f.field_value || ''}
                    onChange={e => handleChange(f, f.id, e.target.value)} />
                )}
                {f.field_type === 'single choice' && (
                  <select className="_tm-select" value={f.field_value || ''}
                    onChange={e => handleChange(f, f.id, e.target.value)}>
                    <option value="">Choose…</option>
                    {String(f.field_options || '').split(',').filter(o => o.trim()).map(o => (
                      <option key={o.trim()} value={o.trim()}>{o.trim()}</option>
                    ))}
                  </select>
                )}
                {f.field_type === 'multiple choice' && (
                  <div className="editaskselect-container _tm-input" style={{ padding: 0 }}>
                    <div className="multi-select" onClick={() => { setshowmcqoptions(f.id); setSelectedOptions(f.field_value ? f.field_value.split(',') : []); }}>
                      <span className="multi-select-text">{f.field_value || 'Choose…'}</span>
                      <ChevronDown size={16} color={C.faint} />
                    </div>
                    {showmcqoptions === f.id && (
                      <div className="multi-select-dropdown" onMouseLeave={() => setshowmcqoptions(-1)}>
                        {String(f.field_options || '').split(',').filter(o => o.trim()).map(o => (
                          <div key={o} className={`option-item${selectedOptions.includes(o) ? ' option-selected' : ''}`}
                            onClick={() => handleOptionClick(f, o)}>
                            <span>{o}</span>
                            {selectedOptions.includes(o) && <span className="option-check"><Check size={14} /></span>}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </TaskModal>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// ADD TASK MODAL
// ─────────────────────────────────────────────────────────────────────────────
export const AddTaskModal = ({
  addtask, setaddtask,
  task_name, settask_name,
  task_user, task_user_list, settask_user,
  task_status, settask_status,
  task_priority, settask_priority,
  task_due_date, settask_due_date,
  task_note, settask_note,
  alladdtionalfields, alladdtionalfieldsvalues, setalladdtionalfieldsvalues,
  create_task, setloading,
}) => {
  const [lastid, setlastid] = useState(0);
  const getId = () => { setlastid(p => p + 1); return lastid + 1; };

  const filteredFields = useMemo(() => {
    return alladdtionalfields.map(field => {
      const match = alladdtionalfieldsvalues.find(v => v.field_id === field.id);
      if (!match) return { id: `New ${getId()}`, field_id: field.id, field_name: field.name, field_type: field.type, field_options: field.options, field_value: '' };
      return { ...match, field_name: field.name, field_type: field.type, field_options: field.options };
    });
  }, [alladdtionalfields, alladdtionalfieldsvalues]);

  const handleChange = (fieldXX, field_id, field_value) => {
    let booleannew = 1;
    let updated = alladdtionalfieldsvalues.map(f => {
      if (f.id === field_id) { booleannew = 0; return { ...f, field_value }; }
      return f;
    });
    if (field_id.includes('New') && booleannew === 1) {
      updated = [...updated, { id: field_id, field_id: fieldXX.field_id, field_name: fieldXX.field_name, field_type: fieldXX.field_type, field_value, field_options: fieldXX.field_options }];
    }
    setalladdtionalfieldsvalues(updated);
  };

  const [showmcqoptions, setshowmcqoptions] = useState(-1);
  const [selectedOptions, setSelectedOptions] = useState([]);

  const handleOptionClick = (fieldXX, value) => {
    setSelectedOptions(prev => {
      const next = prev.includes(value) ? prev.filter(o => o !== value) : [...prev, value];
      const str = next.join(',').replace(/^,/, '');
      handleChange(fieldXX, fieldXX.id, str);
      return next;
    });
  };

  return (
    <TaskModal
      show={addtask}
      onHide={() => setaddtask(false)}
      title="Add Task"
      subtitle="Fill in the details to create a new task"
      footer={
        <button className="_tm-btn-primary" onClick={create_task}>
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
          </svg>
          Create Task
        </button>
      }
    >
      <div className="_tm-section-card" style={{ marginBottom: 18 }}>
        <div className="_tm-form-grid">
          <div>
            <FormLabel>📌 Task</FormLabel>
            <input className="_tm-input" value={task_name || ''} placeholder="Activity"
              onChange={e => settask_name(e.target.value)} />
          </div>
          <div>
            <FormLabel statusType="priority" statusValue={task_priority}>Priority</FormLabel>
            <select className="_tm-select" value={task_priority || ''} onChange={e => settask_priority(e.target.value)}>
              <option value="">Select priority…</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
          </div>
          <div>
            <FormLabel>👥 Assigned To</FormLabel>
            <select className="_tm-select" value={task_user || ''} onChange={e => settask_user(e.target.value)}>
              <option value="">Assign to…</option>
              {task_user_list.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </div>
          <div>
            <FormLabel statusType="status" statusValue={task_status}>Status</FormLabel>
            <select className="_tm-select" value={task_status || ''} onChange={e => settask_status(e.target.value)}>
              <option value="">Select status…</option>
              <option value="done">Done</option>
              <option value="in-progress">In Progress</option>
              <option value="overdue">Overdue</option>
              <option value="pending">Pending</option>
            </select>
          </div>
          <div className="_tm-form-grid-full">
            <FormLabel>📆 Due Date</FormLabel>
            <input className="_tm-input" type="date" value={task_due_date || ''}
              onChange={e => settask_due_date(e.target.value)} />
          </div>
        </div>
      </div>

      <div className="_tm-section-card" style={{ marginBottom: 18 }}>
        <FormLabel>📝 Description (Optional)</FormLabel>
        <div className="_tm-rich-editor-wrap">
          <RichTextEditor value={task_note} onChange={settask_note} placeholder="Add a description…" />
        </div>
      </div>

      {filteredFields.length > 0 && (
        <div className="_tm-section-card">
          <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: '.06em', textTransform: 'uppercase', fontFamily: ff, marginBottom: 14 }}>
            Additional Fields
          </div>
          <div className="_tm-form-grid">
            {filteredFields.map(f => (
              <div key={f.id}>
                <FormLabel>{f.field_name}</FormLabel>
                {f.field_type === 'text' && (
                  <input className="_tm-input" value={f.field_value || ''} placeholder={f.field_name}
                    onChange={e => handleChange(f, f.id, e.target.value)} />
                )}
                {f.field_type === 'date' && (
                  <input className="_tm-input" type="date" value={f.field_value || ''}
                    onChange={e => handleChange(f, f.id, e.target.value)} />
                )}
                {f.field_type === 'single choice' && (
                  <select className="_tm-select" value={f.field_value || ''}
                    onChange={e => handleChange(f, f.id, e.target.value)}>
                    <option value="">Choose…</option>
                    {String(f.field_options || '').split(',').filter(o => o.trim()).map(o => (
                      <option key={o.trim()} value={o.trim()}>{o.trim()}</option>
                    ))}
                  </select>
                )}
                {f.field_type === 'multiple choice' && (
                  <div className="editaskselect-container _tm-input" style={{ padding: 0 }}>
                    <div className="multi-select" onClick={() => { setshowmcqoptions(f.id); setSelectedOptions(f.field_value ? f.field_value.split(',') : []); }}>
                      <span className="multi-select-text">{f.field_value || 'Choose…'}</span>
                      <ChevronDown size={16} color={C.faint} />
                    </div>
                    {showmcqoptions === f.id && (
                      <div className="multi-select-dropdown" onMouseLeave={() => setshowmcqoptions(-1)}>
                        {String(f.field_options || '').split(',').filter(o => o.trim()).map(o => (
                          <div key={o} className={`option-item${selectedOptions.includes(o) ? ' option-selected' : ''}`}
                            onClick={() => handleOptionClick(f, o)}>
                            <span>{o}</span>
                            {selectedOptions.includes(o) && <span className="option-check"><Check size={14} /></span>}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </TaskModal>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// ADD GROUP MODAL
// ─────────────────────────────────────────────────────────────────────────────
export const AddGroupModal = ({
  addgroup, setaddgroup,
  group_name, setgroup_name,
  create_group, setloading,
}) => (
  <TaskModal
    show={addgroup}
    onHide={() => setaddgroup(false)}
    title="Add Group"
    subtitle="Create a new task group"
    small
    footer={
      <button className="_tm-btn-primary" onClick={create_group}>
        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
        </svg>
        Create Group
      </button>
    }
  >
    <div className="_tm-section-card">
      <FormLabel>📌 Group Name</FormLabel>
      <input className="_tm-input" value={group_name || ''} placeholder="Enter group name…"
        onChange={e => setgroup_name(e.target.value)} />
    </div>
  </TaskModal>
);

// ─────────────────────────────────────────────────────────────────────────────
// UPDATE GROUP MODAL
// ─────────────────────────────────────────────────────────────────────────────
export const UpdateGroupModal = ({
  updategroup, setupdategroup,
  group_name, setgroup_name,
  update_group, setloading,
}) => (
  <TaskModal
    show={updategroup}
    onHide={() => setupdategroup(false)}
    title="Update Group"
    subtitle="Rename or edit this group"
    small
    footer={
      <button className="_tm-btn-primary" onClick={update_group}>
        <SaveIcon /> Save Changes
      </button>
    }
  >
    <div className="_tm-section-card">
      <FormLabel>📌 Group Name</FormLabel>
      <input className="_tm-input" value={group_name || ''} placeholder="Enter group name…"
        onChange={e => setgroup_name(e.target.value)} />
    </div>
  </TaskModal>
);

// ─────────────────────────────────────────────────────────────────────────────
// SHOW DESCRIPTION MODAL
// ─────────────────────────────────────────────────────────────────────────────
export const ShowDescription = ({
  showdescription, setshowdescription,
  description, setdescription,
  setloading, update_description,
}) => (
  <TaskModal
    show={showdescription}
    onHide={() => setshowdescription(false)}
    title="Description"
    subtitle="Edit the rich text description below"
    footer={
      <button className="_tm-btn-primary" onClick={update_description}>
        <SaveIcon /> Update
      </button>
    }
  >
    <div className="_tm-section-card">
      <FormLabel>📝 Description</FormLabel>
      <div className="_tm-rich-editor-wrap">
        <RichTextEditor value={description} onChange={setdescription} placeholder="Write a description…" />
      </div>
    </div>
  </TaskModal>
);

// ─────────────────────────────────────────────────────────────────────────────
// SHOW SHARE MODAL
// ─────────────────────────────────────────────────────────────────────────────
export const ShowShareModal = ({
  shareshow, setshareshow,
  shareusername, setshareusername,
  shareemailid, setshareemailid,
  shareuseraccess, setshareuseraccess,
  setloading, grant_access, ShareLink,
}) => (
  <TaskModal
    show={shareshow}
    onHide={() => setshareshow(false)}
    title="Share"
    subtitle="Invite someone and set their access level"
    footer={
      <button className="_tm-btn-primary" onClick={grant_access} style={{ minWidth: 140 }}>
        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
          <line x1="19" y1="8" x2="19" y2="14"/><line x1="22" y1="11" x2="16" y2="11"/>
        </svg>
        Grant Access
      </button>
    }
  >
    <div className="_tm-section-card" style={{ marginBottom: 18 }}>
      <div className="_tm-form-grid">
        <div>
          <FormLabel>👤 Name</FormLabel>
          <input className="_tm-input" value={shareusername || ''} placeholder="First Name"
            onChange={e => setshareusername(e.target.value)} />
        </div>
        <div>
          <FormLabel>✉️ Email</FormLabel>
          <input className="_tm-input" type="email" value={shareemailid || ''} placeholder="Email address"
            onChange={e => setshareemailid(e.target.value)} />
        </div>
        <div className="_tm-form-grid-full">
          <FormLabel>🔒 Access Level</FormLabel>
          <select className="_tm-select" value={shareuseraccess || ''} onChange={e => setshareuseraccess(e.target.value)}>
            <option value="">Choose access level…</option>
            <option value="View">View</option>
            <option value="Edit">Edit</option>
          </select>
        </div>
      </div>
    </div>

    <div className="_tm-share-row">
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={C.indigo600} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
        <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
      </svg>
      <span style={{ flex: 1, fontSize: 13, color: C.indigo700, fontFamily: ff, fontWeight: 500 }}>
        Anyone with the link can <strong>View</strong>
      </span>
      <button className="_tm-copy-btn" onClick={ShareLink}>Copy Link</button>
    </div>
  </TaskModal>
);

// ─────────────────────────────────────────────────────────────────────────────
// SHOW EDIT ACCESS MODAL
// ─────────────────────────────────────────────────────────────────────────────
export const ShowEditAccessModal = ({
  showeditaccess, setshoweditaccess,
  shareusername, setshareusername,
  shareemailid, setshareemailid,
  shareuseraccess, setshareuseraccess,
  setloading, edit_access,
}) => (
  <TaskModal
    show={showeditaccess}
    onHide={() => setshoweditaccess(false)}
    title="Edit Access"
    subtitle="Update this user's access permissions"
    footer={
      <button className="_tm-btn-primary" onClick={edit_access} style={{ minWidth: 140 }}>
        <SaveIcon /> Save Changes
      </button>
    }
  >
    <div className="_tm-section-card">
      <div className="_tm-form-grid">
        <div>
          <FormLabel>👤 Name</FormLabel>
          <input className="_tm-input" value={shareusername || ''} placeholder="First Name"
            onChange={e => setshareusername(e.target.value)} />
        </div>
        <div>
          <FormLabel>✉️ Email</FormLabel>
          <input className="_tm-input" type="email" value={shareemailid || ''} placeholder="Email address"
            onChange={e => setshareemailid(e.target.value)} />
        </div>
        <div className="_tm-form-grid-full">
          <FormLabel>🔒 Access Level</FormLabel>
          <select className="_tm-select" value={shareuseraccess || ''} onChange={e => setshareuseraccess(e.target.value)}>
            <option value="">Choose access level…</option>
            <option value="View">View</option>
            <option value="Edit">Edit</option>
          </select>
        </div>
      </div>
    </div>
  </TaskModal>
);

// ─────────────────────────────────────────────────────────────────────────────
// UPLOAD KNOWLEDGE BASE MODAL
// ─────────────────────────────────────────────────────────────────────────────
export const UploadKnowledgebaseModal = ({
  showkb, setshowkb,
  kbfilename, setkbfilename,
  kbdescription, setkbdescription,
  kbtags, allavailabletags, setkbtags,
  kburl, seturl,
  kbfiles, kbfileInputRef, setkbfiles,
  uploaddsrknowledgebaseresource,
  uploaddsrknowledgebaseresourcelink,
  setloading,
}) => {
  const [wannaupload, setwannaupload] = useState('no');
  const [dragActive, setDragActive] = useState(false);

  const handleDrag = e => {
    e.preventDefault(); e.stopPropagation();
    setDragActive(e.type === 'dragenter' || e.type === 'dragover');
  };
  const handleDrop = e => {
    e.preventDefault(); e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files?.[0]) setkbfiles(e.dataTransfer.files);
  };

  const handleSubmit = () => {
    wannaupload === 'yes' ? uploaddsrknowledgebaseresource() : uploaddsrknowledgebaseresourcelink();
  };

  return (
    <TaskModal
      show={showkb}
      onHide={() => setshowkb(false)}
      title="Add Knowledge Base Resource"
      subtitle="Upload a file or link an external resource"
      footer={
        <button className="_tm-btn-primary" onClick={handleSubmit}>
          <Upload size={14} /> Upload Resource
        </button>
      }
    >
      <div className="_tm-section-card" style={{ marginBottom: 18 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <FormLabel>Resource Name</FormLabel>
            <input className="_tm-input" value={kbfilename || ''} placeholder="Resource Name"
              onChange={e => setkbfilename(e.target.value)} />
          </div>
          <div>
            <FormLabel>Resource Description</FormLabel>
            <textarea className="_tm-textarea" value={kbdescription || ''} placeholder="Describe this resource…"
              onChange={e => setkbdescription(e.target.value)} />
          </div>
          <div>
            <FormLabel>Upload or Link?</FormLabel>
            <select className="_tm-select" value={wannaupload} onChange={e => setwannaupload(e.target.value)}>
              <option value="no">Enter the URL for the resource</option>
              <option value="yes">Upload a file</option>
            </select>
          </div>
        </div>
      </div>

      {wannaupload === 'yes' ? (
        <div
          className={`upload-area${dragActive ? ' drag-active' : ''}`}
          onDragEnter={handleDrag} onDragLeave={handleDrag}
          onDragOver={handleDrag} onDrop={handleDrop}
        >
          <div className="upload-area-content">
            <Upload className="upload-icon" size={44} />
            <p className="upload-title">Drag & drop files here, or browse</p>
            <p className="upload-subtitle">PDF, Images, Spreadsheets, Videos, Audio and more</p>
            {kbfiles && kbfiles.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, justifyContent: 'center', marginTop: 4 }}>
                {Array.from(kbfiles).map((f, i) => (
                  <span key={i} style={{ padding: '4px 10px', borderRadius: 99, background: C.indigo100, color: C.indigo700, fontSize: 12, fontWeight: 600, fontFamily: ff, border: `1px solid ${C.indigo200}` }}>
                    {f.name}
                  </span>
                ))}
              </div>
            )}
            <button type="button" className="choose-files-btn" onClick={() => kbfileInputRef.current?.click()}>
              Choose Files
            </button>
          </div>
        </div>
      ) : (
        <div className="_tm-section-card">
          <FormLabel>🔗 Resource URL</FormLabel>
          <textarea className="_tm-textarea" value={kburl || ''} placeholder="https://…"
            onChange={e => seturl(e.target.value)} />
        </div>
      )}

      <input
        ref={kbfileInputRef} type="file" multiple accept="*/*"
        onChange={e => setkbfiles(e.target.files)}
        style={{ display: 'none' }}
      />
    </TaskModal>
  );
};